import { useQueryClient } from "@tanstack/react-query";
import { ClientSearchForm } from "@/components/clients/ClientSearchForm";
import { ClientSearchResults } from "@/components/clients/ClientSearchResults";
import { LastSearchBanner } from "@/components/shared/last-search-banner";
import { PageHeader } from "@/components/shared/page-header";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { type ActiveBuInstance, useActiveBuInstances } from "@/hooks/use-bu-instances";
import {
  useClearLastClientSearch,
  useLastClientSearch,
  useSearchClients,
} from "@/hooks/use-client-search";
import type { SearchClientsResult } from "@/lib/coach-manager/client";
import { clientSearchKeys } from "@/lib/query";
import type { ClientSearchRequest } from "@/schemas/clients";

const OPERATOR_LABELS: Record<string, string> = {
  GreaterThan: "after",
  EqualTo: "on",
  LessThan: "before",
};

function buildCriteriaFields(
  criteria: ClientSearchRequest,
  buInstances: ActiveBuInstance[] | undefined,
  buInstanceLabel: string,
): Array<{ label: string; value: string }> {
  const bu = buInstances?.find((b) => b.id === criteria.buInstanceId);
  const fields: Array<{ label: string; value: string }> = [
    { label: buInstanceLabel, value: bu?.name ?? criteria.buInstanceId },
  ];

  if (criteria.clientId) fields.push({ label: "Client ID", value: criteria.clientId });
  if (criteria.surname) fields.push({ label: "Surname", value: criteria.surname });
  if (criteria.company) fields.push({ label: "Company", value: criteria.company });
  if (criteria.firstName) fields.push({ label: "First Name", value: criteria.firstName });
  if (criteria.initials) fields.push({ label: "Initials", value: criteria.initials });
  if (criteria.address) fields.push({ label: "Address", value: criteria.address });
  if (criteria.postCode) fields.push({ label: "Post Code", value: criteria.postCode });
  if (criteria.telNo) fields.push({ label: "Tel No", value: criteria.telNo });
  if (criteria.email) fields.push({ label: "Email", value: criteria.email });
  if (criteria.dateCreated) {
    const op = criteria.dateCreatedOperator
      ? OPERATOR_LABELS[criteria.dateCreatedOperator]
      : undefined;
    fields.push({
      label: "Date Created",
      value: op ? `${op} ${criteria.dateCreated}` : criteria.dateCreated,
    });
  }
  if (criteria.lastUpdated) {
    const op = criteria.lastUpdatedOperator
      ? OPERATOR_LABELS[criteria.lastUpdatedOperator]
      : undefined;
    fields.push({
      label: "Last Updated",
      value: op ? `${op} ${criteria.lastUpdated}` : criteria.lastUpdated,
    });
  }

  return fields;
}

export function ClientSearchPage() {
  const queryClient = useQueryClient();
  const { label } = useBuInstanceLabel();
  const { data: buInstances } = useActiveBuInstances();
  const search = useSearchClients();
  const lastSearch = useLastClientSearch();
  const clearLastSearch = useClearLastClientSearch();

  function handleSearch(values: ClientSearchRequest) {
    search.mutate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: clientSearchKeys.last });
      },
    });
  }

  function handleClear() {
    clearLastSearch.mutate();
    search.reset();
  }

  // Bridges the gap between "mutation resolved" and "invalidated lastSearch
  // query refetching" so old results don't flash before the fresh ones land —
  // pure derived state, no extra useState.
  const isPending = search.isPending || (search.isSuccess && lastSearch.isFetching);

  const state = lastSearch.data;
  const result: SearchClientsResult | undefined = state
    ? state.status === "success"
      ? { ok: true, clients: state.clients ?? [], resultDescription: "" }
      : { ok: false, error: state.errorMessage ?? "Search failed" }
    : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Client Search"
        description={`Search a Coach Manager ${label} for a Client. Results are cached locally for the Copy Contract Target Client picker.`}
      />
      <ClientSearchForm onSearch={handleSearch} isSearching={search.isPending} />
      {state && !isPending && (
        <LastSearchBanner
          fields={buildCriteriaFields(state.criteria, buInstances, label)}
          searchedAt={state.searchedAt}
          onClear={handleClear}
          isClearing={clearLastSearch.isPending}
        />
      )}
      <ClientSearchResults
        result={result}
        isPending={isPending}
        error={search.error ?? lastSearch.error}
      />
    </div>
  );
}
